import { useState, useEffect } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { inventoryApi } from '@/api/client';
import type { Inventory } from '@/types';
import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import clsx from 'clsx';

export function InventoryPage() {
  const [inventory, setInventory] = useState<Inventory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showZero, setShowZero] = useState(false);
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchInventory = async () => {
    setIsLoading(true);
    const response = await inventoryApi.list({
      search,
      show_zero: showZero,
      expiring_only: expiringOnly,
      page,
      limit: 50,
    });

    if (response.success && response.data) {
      setInventory(response.data.items);
      setTotalPages(response.data.totalPages);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchInventory();
  }, [search, showZero, expiringOnly, page]);

  const getExpiryStatus = (item: Inventory) => {
    if (!item.expiry_date || item.days_until_expiry === null || item.days_until_expiry === undefined) return null;
    
    const days = item.days_until_expiry;
    if (days < 0) {
      return { label: '期限切れ', class: 'badge-danger' };
    }
    if (days === 0) {
      return { label: '本日期限', class: 'badge-danger' };
    }
    if (days <= (item.expiry_alert_days || 7)) {
      return { label: `あと${days}日`, class: 'badge-warning' };
    }
    return { label: `あと${days}日`, class: 'badge-info' };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">在庫一覧</h1>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="商品名・ロット番号で検索..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="input pl-10"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showZero}
                onChange={(e) => {
                  setShowZero(e.target.checked);
                  setPage(1);
                }}
                className="rounded border-gray-300"
              />
              在庫0も表示
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={expiringOnly}
                onChange={(e) => {
                  setExpiringOnly(e.target.checked);
                  setPage(1);
                }}
                className="rounded border-gray-300"
              />
              期限切れ間近のみ
            </label>
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : inventory.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            在庫データがありません
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>商品名</th>
                  <th>カテゴリ</th>
                  <th>ロット番号</th>
                  <th className="text-right">数量</th>
                  <th>賞味期限</th>
                  <th>入庫日</th>
                  <th>メモ</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {inventory.map((item) => {
                  const expiryStatus = getExpiryStatus(item);
                  return (
                    <tr key={item.id} className={clsx(
                      item.quantity === 0 && 'bg-gray-50 text-gray-400'
                    )}>
                      <td className="font-medium">{item.product_name}</td>
                      <td>{item.category_name || '-'}</td>
                      <td className="font-mono text-sm">{item.lot_number}</td>
                      <td className="text-right">
                        <span className={clsx(
                          'font-medium',
                          item.quantity === 0 && 'text-gray-400'
                        )}>
                          {item.quantity}
                        </span>
                        <span className="text-gray-500 ml-1">{item.product_unit}</span>
                      </td>
                      <td>
                        {item.expiry_date ? (
                          <div className="flex items-center gap-2">
                            <span>{format(parseISO(item.expiry_date), 'yyyy/MM/dd', { locale: ja })}</span>
                            {expiryStatus && (
                              <span className={expiryStatus.class}>
                                {expiryStatus.label}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td>
                        {format(parseISO(item.received_date), 'yyyy/MM/dd', { locale: ja })}
                      </td>
                      <td className="max-w-[200px] truncate text-gray-500">
                        {item.note || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary px-3 py-1 text-sm"
            >
              前へ
            </button>
            <span className="text-sm text-gray-600">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn-secondary px-3 py-1 text-sm"
            >
              次へ
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
